import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorHandler } from '@angular/core';
import { Injectable, Injector, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Actions } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import * as Sentry from '@sentry/browser';
import type { StartSpanOptions } from '@sentry/core';
import { config } from 'rxjs';

import { getPathFromRoute } from '../helpers';
import { AccountBase, SentryConfig } from './config';
import { RtLocation } from './location.service';

const injectActions = () => {
  try {
    return inject(Actions, { optional: true });
  } catch {
    return null;
  }
};

enum TransactionOp {
  PAGE_LOAD = 'pageload',
  NAVIGATION = 'navigation',
}

@Injectable()
export class RtcErrorHandler implements ErrorHandler {
  private readonly sentryConfig = inject(SentryConfig);
  private readonly injector = inject(Injector);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(RtLocation);

  private readonly NGRX_ACTIONS_CONTEXT_SIZE = 25;
  private readonly filteredStatuses: number[] = [0, 401, 403, 404];
  private readonly appRevision: string = this.injector.get(this.sentryConfig.revisionToken);
  private readonly ignoredEvents: Array<string | RegExp> = [
    'Http request failed',
    'ChunkLoadError',
    'HTTP request failed',
    'Socket close code 1000',
    '[Analytics] Segment client failed to load',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
    'channel failed because of timeout',
  ];
  private nextPath: string;
  private actions$ = injectActions();
  private ngrxActions: Action[] = [];

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    const sentryConfig = this.sentryConfig;

    if (!this.sentryConfig.enable) {
      return;
    }

    this.location.go$.subscribe(path => (this.nextPath = path));

    config.onUnhandledError = (err: any) => this.rxjsOnUnhandledError(err);

    Sentry.init({
      dsn: this.sentryConfig.dsn,
      release: this.appRevision,
      integrations: [
        Sentry.rewriteFramesIntegration({
          iteratee: frame => {
            if (frame.filename) {
              frame.in_app = /(.*)(runtime|main|common|\d+)\.(.*)\.js/.test(frame.filename);
            }
            return frame;
          },
        }),
        Sentry.browserTracingIntegration({
          enableLongAnimationFrame: false,
          ignoreResourceSpans: [
            'resource.script',
            'resource.css',
            'resource.link',
            'resource.other',
            'resource.img',
            'resource.icon',
            'resource.iframe',
          ],
          beforeStartSpan: (context: StartSpanOptions): StartSpanOptions => ({
            ...context,
            ...this.getTransactionName(context),
          }),
        }),
      ],
      ignoreSpans: [{ op: /^browser\./ }, { op: 'mark' }, { op: 'measure' }],
      tracesSampler: samplingContext => {
        if (TransactionOp.PAGE_LOAD === samplingContext.name) {
          return 0.02;
        }
        if (samplingContext.attributes?.['sentry.origin']?.includes(TransactionOp.NAVIGATION)) {
          return 0.01;
        }

        return 0.5;
      },
      beforeBreadcrumb: (breadcrumb, hint) =>
        // This will filter out clicks/typing
        breadcrumb.category && breadcrumb.category.includes('ui.') ? null : breadcrumb,
      beforeSend: (event, hint) => {
        if (!this.sentryConfig.enable) return null;
        // @ts-ignore
        const httpStatus = event.extra?.__serialized__?.status;
        const isStatusValid =
          httpStatus === undefined || !this.filteredStatuses.includes(httpStatus);

        return isStatusValid ? event : null;
      },
      maxBreadcrumbs: 30,
      ignoreErrors: this.ignoredEvents,
      environment: sentryConfig.environmentName,
    });

    if (this.actions$) {
      this.actions$.subscribe(
        ({ type }) =>
          (this.ngrxActions = [{ type }, ...this.ngrxActions].slice(
            0,
            this.NGRX_ACTIONS_CONTEXT_SIZE,
          )),
      );
    }

    /**
     * NgRx store devtools requires ErrorService
     * Here we need currentAdmin but it is empty because AccountBase is not initialized yet
     * setTimeout fixes this issue
     */
    setTimeout(() => {
      const account = this.injector.get(AccountBase);
      account.currentAdmin$.subscribe(currentAdmin =>
        Sentry.setUser({
          id: `${currentAdmin.id}`,
          email: currentAdmin.email,
          username: `${currentAdmin.firstName} ${currentAdmin.lastName}`,
          roleName: currentAdmin.roleName,
        }),
      );

      account.currentCompany$.subscribe(currentCompany =>
        Sentry.setExtra('Company', currentCompany?.id),
      );
    });
  }

  public handleError(err: Error, showInConsole: boolean = !this.sentryConfig.enable): void {
    try {
      if (this.sentryConfig.enable) {
        if (err instanceof HttpErrorResponse) {
          if (!this.filteredStatuses.includes(err.status)) {
            Sentry.captureMessage(this.getHttpErrorResponseMessage(err));
          }
        } else {
          this.sendErrorToSentry(err);
        }
      }
    } finally {
      if (showInConsole) {
        console.error(err);
      }
    }
  }

  public trace(name: string, showInConsole: boolean): void {
    try {
      throw new Error(name);
    } catch (e) {
      this.handleError(e, showInConsole);
    }
  }

  private rxjsOnUnhandledError(err: any) {
    if (err instanceof Error) return this.handleError(err);

    try {
      const message = this.getErrorTypeMessage(err);
      const isIgnored = this.ignoredEvents.some(stringOrRegexp =>
        typeof stringOrRegexp === 'string'
          ? message.includes(stringOrRegexp)
          : stringOrRegexp.test(message),
      );
      if (isIgnored) return;

      throw new Error(`Unhandled rxjs error ${message}`);
    } catch (e) {
      this.handleError(e);
    }
  }

  private getErrorTypeMessage(err: any) {
    if (err instanceof HttpErrorResponse) {
      return this.getHttpErrorResponseMessage(err);
    } else if (typeof err === 'object' && err !== null) {
      return `keys: ${Object.keys(err)}`;
    } else {
      return `value: ${err}`;
    }
  }

  private getHttpErrorResponseMessage(response: HttpErrorResponse): string {
    return `Http request failed. (${response.url}, ${response.status}, ${response.message})`;
  }

  private sendErrorToSentry(err: any): void {
    Sentry.setTags({ fatal: 'true' });
    Sentry.captureException(err.originalError || err, {
      contexts: {
        ngrxActions: {
          actions: JSON.stringify(this.ngrxActions, null, 2),
        },
      },
    });
  }

  private getTransactionName(context: StartSpanOptions): StartSpanOptions {
    if (context.op === TransactionOp.PAGE_LOAD) {
      return { name: context.op };
    }

    const { queryParams: nextQueryParams } = this.router.parseUrl(this.nextPath);
    if (nextQueryParams.candidate) {
      return {
        name: [
          '/candidate_dialog',
          nextQueryParams.tab || 'overview',
          nextQueryParams.openInterview === 'true' && 'open_interview',
        ]
          .filter(Boolean)
          .join('/'),
      };
    }

    return { name: '/' + getPathFromRoute(this.route) };
  }
}
